/**
 * The question a window of rows is the answer to.
 *
 * Every fetch, every cached row and every response in flight belongs to one of
 * these. Reduced to a string, it becomes the window's `signature`: a response
 * that comes back carrying a different one is answering a question nobody is
 * asking any more and is dropped rather than folded in.
 *
 * Upstream compared the view id alone, which catches switching views but not a
 * filter changing while a scroll fetch is in flight -- the fetch then lands rows
 * the new filter excludes, and they sit in the grid until something else
 * dislodges them. Signature-based invalidation is the one mechanism: change what
 * the view asks for and the window refreshes itself, with no explicit fan-out
 * from the mutation that changed it.
 */
import type { ViewFilter, ViewFilterGroup, ViewGroupBy, ViewSort } from '@/api/types'

/**
 * Which search implementation the backend should use.
 *
 * Decided by the deployment, not by the reader: `pg_fulltext_search_enabled` on
 * `GET /api/settings/` says which one this instance has. It is part of the query
 * because the two modes genuinely return different rows.
 */
export type SearchMode = 'full-text-with-count' | 'compat'

/** The parts of a view that decide which rows come back, and in what order. */
export interface QueryableView {
  /**
   * The view's identity, which is not always a number.
   *
   * A publicly shared view is addressed by its slug and never says what its id
   * is -- `PublicViewSerializer` maps `id` onto the slug on purpose, and reports
   * a placeholder for the table, so that a link cannot be turned into knowledge
   * of what else the workspace holds. The slug is a perfectly good identity for
   * the window: it is what the signature is built from and what the fetcher
   * addresses.
   */
  id: number | string
  filters_disabled?: boolean
  filter_type?: string
  filters?: readonly ViewFilter[]
  filter_groups?: readonly ViewFilterGroup[]
  sortings?: readonly ViewSort[]
  group_bys?: readonly ViewGroupBy[]
}

/** What the reader is searching for, if anything. */
export interface RowSearch {
  term: string
  mode: SearchMode
}

export interface RowQuery {
  /** The view's id, or a public view's slug. See `QueryableView`. */
  viewId: number | string
  search: RowSearch
  /**
   * What the view is grouped by, in the order the backend applies it.
   *
   * Here because a *publicly* shared view has to send it: the reader there is
   * never told the view's filters and the endpoint applies its own, but the
   * group by is asked for explicitly, since the counts it returns are extra
   * work nobody unwilling to draw them should pay for. The signed-in endpoint
   * reads the view's own and ignores this.
   *
   * It cannot fall out of step with the signature, because everything about a
   * group by that matters is already in it.
   */
  groupBys: readonly ViewGroupBy[]
  /** The identity of this question; see the module comment. */
  signature: string
}

/**
 * Reduces a filter to the parts that change which rows match.
 *
 * `preload_values` is deliberately dropped. It carries display material for the
 * filter's own editor -- the names behind a set of linked row ids, so the filter
 * form can show them without a second request -- and it can arrive, change or go
 * missing without a single row changing. Including it would throw the window
 * away every time somebody opened a filter menu.
 */
const filterParts = (filter: ViewFilter) => [
  filter.id,
  filter.field,
  filter.type,
  filter.value ?? '',
  filter.group ?? null,
]

const groupParts = (group: ViewFilterGroup) => [
  group.id,
  group.filter_type ?? 'AND',
  group.parent_group ?? null,
]

/**
 * Reduces a sort or a group-by to the parts that change the ordering.
 *
 * `width` is on a group-by and is not here: it is how wide to draw the group
 * column, which no row cares about. `priority` is not here either, for a
 * subtler reason -- the terms are emitted in priority order, so the *relative*
 * order is already in the signature and the numbers themselves are not. Two
 * sorts renumbered from 0,1 to 0,5 are applied in the same order and are the
 * same question; swap them and the emitted order swaps with them.
 */
const orderParts = (term: ViewSort | ViewGroupBy) => [
  term.id,
  term.field,
  term.order ?? 'ASC',
  term.type ?? '',
]

/**
 * Orders terms by what the backend orders them by, not by how they arrived.
 *
 * Two responses can list the same sorts in a different array order while meaning
 * exactly the same thing. Sorting here means that costs nothing; leaving it out
 * would refetch the whole window because a list came back shuffled.
 */
const byPriority = (a: ViewSort | ViewGroupBy, b: ViewSort | ViewGroupBy) =>
  a.priority - b.priority || a.id - b.id

const byId = (a: { id: number }, b: { id: number }) => a.id - b.id

/**
 * Which *rows* a view answers with, without regard to their order.
 *
 * The narrower half of the signature, and the one anything that reduces the
 * rows to a single number wants: an aggregation over a set does not care what
 * order the set is in, so sorting or grouping a view must not send the footer
 * off to ask again.
 *
 * Filters are in it even though the backend applies the view's own server-side
 * and they are not sent as request parameters. This is not a description of the
 * request; it is a description of the answer.
 */
export function rowMembershipSignature(view: QueryableView, search: RowSearch): string {
  const disabled = view.filters_disabled === true

  return JSON.stringify({
    view: view.id,
    // A view with its filters switched off returns every row, so the filters it
    // is holding onto make no difference to the answer while it stays off.
    filterType: disabled ? null : (view.filter_type ?? 'AND'),
    filters: disabled ? [] : [...(view.filters ?? [])].sort(byId).map(filterParts),
    filterGroups: disabled
      ? []
      : [...(view.filter_groups ?? [])].sort(byId).map(groupParts),
    search: search.term,
    // Only when there is something to search for: the mode is a deployment
    // setting, and changing it while the search box is empty changes nothing.
    searchMode: search.term === '' ? null : search.mode,
  })
}

/**
 * The identity of a query, as a string that can be compared with `===`.
 *
 * Two rules decide what belongs in it. Anything that changes *which rows come
 * back, or in what order* must be in it, or a stale window survives a change it
 * should not have. Anything cosmetic must not be, or the window is thrown away
 * and refetched every time somebody drags a column wider.
 *
 * Built *from* the membership signature rather than beside it, so the two
 * cannot drift: a filter added to one is in the other by construction.
 */
export function rowQuerySignature(view: QueryableView, search: RowSearch): string {
  return JSON.stringify({
    rows: rowMembershipSignature(view, search),
    groupBys: [...(view.group_bys ?? [])].sort(byPriority).map(orderParts),
    sortings: [...(view.sortings ?? [])].sort(byPriority).map(orderParts),
  })
}

export function rowQuery(view: QueryableView, search: RowSearch): RowQuery {
  return {
    viewId: view.id,
    search,
    groupBys: [...(view.group_bys ?? [])].sort(byPriority),
    signature: rowQuerySignature(view, search),
  }
}
